/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode } from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { GlobalShortcutsItem } from '@atlassian/global-shortcuts';
import type { NavItemConfig } from '@atlassian/navigation-common';

export function getMenuIconAndLabel(
	items: Record<string, NavItemConfig>,
	shortcuts: Record<string, GlobalShortcutsItem>,
) {
	return (menuId: string): { icon: ReactNode; label: string } => {
		const item = items[menuId];

		if (item) {
			const { elemBefore: icon, label } = item;
			return { icon, label };
		}

		const shortcut = shortcuts[menuId];

		if (shortcut) {
			const { icon, label } = shortcut;
			return { icon: ShortcutWrapper(icon), label };
		}

		throw Error(`Menu id ${menuId} not found in either items or shortcuts`);
	};
}

// Add 2px padding around all shortcut icons, as their xsmall-size is 20px. For the app-items,
// their size is 16px, with space="spacious" (taking them to 24px). This added padding means
// the app item icons and shortcut icons should have the same size-including-padding.
const styles = cssMap({
	box: {
		display: 'flex',
		alignContent: 'center',
		paddingTop: token('space.025'),
		paddingRight: token('space.025'),
		paddingBottom: token('space.025'),
		paddingLeft: token('space.025'),
	},
});

function ShortcutWrapper(icon: ReactNode) {
	return <Box xcss={styles.box}> {icon}</Box>;
}
